import { useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Stat, fmtNum, relTime } from '../../components/ui.jsx'

const bytes = (n) => {
  if (n == null) return '—'
  const u = ['B', 'kB', 'MB', 'GB', 'TB']
  let i = 0
  let v = Number(n)
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`
}
const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)} %`)

export function Metricas() {
  const { slug } = useParams()
  const q = useQuery({ queryKey: ['metrics', slug], queryFn: () => api.metrics(slug), refetchInterval: 30_000 })

  if (q.isPending) return <Spinner />
  if (q.isError) return <div className="alert error">{q.error.body?.detail ?? 'No se pudieron leer las métricas.'}</div>

  const { db, funciones: fns, aplicacion } = q.data

  return (
    <>
      <h1 className="h1">Métricas</h1>
      <p className="lede">Uso real del proyecto. Cada bloque dice de dónde salen sus números.</p>

      {/* ── base de datos ─────────────────────────────────────────────────── */}
      <div className="section">
        <div className="section-head">
          <h2 className="h2">Base de datos</h2>
          <span className="hint">
            {(db.desde ? `acumulado desde ${relTime(db.desde)} · ` : '') + 'solo la actividad de tus tablas'}
          </span>
        </div>

        {db.error ? <div className="alert error">{db.error}</div> : (
          <>
            <div className="grid-stats">
              <Stat label="Tus tablas" value={bytes(db.bytesProyecto)}
                    sub={`${fmtNum(db.tablas)} tablas · base entera ${bytes(db.bytes)}`} />
              <Stat label="Leído de disco" value={bytes(db.leidoDisco)} sub={`caché ${pct(db.aciertoCache)}`} />
              <Stat label="Servido de caché" value={bytes(db.servidoCache)} />
              <Stat label="Filas escritas" value={fmtNum(db.filasEscritas)}
                    sub={`${fmtNum(db.insertadas)} alta · ${fmtNum(db.actualizadas)} cambio · ${fmtNum(db.borradas)} baja`} />
              <Stat label="Filas leídas" value={fmtNum(db.filasDevueltas)} />
            </div>

            {/* Lo que Postgres no sabe atribuir a una tabla se enseña aparte y con la
                advertencia puesta: aquí sí cuentan la consola y el autovacuum. */}
            <div className="hint" style={{ margin: '14px 0 8px' }}>
              Base entera — incluye a la consola y al autovacuum
            </div>
            <div className="grid-stats">
              <Stat label="Transacciones" value={fmtNum(db.transacciones)}
                    sub={db.rollbacks ? `${fmtNum(db.rollbacks)} deshechas` : 'sin rollbacks'} />
              <Stat label="Conexiones" value={`${db.conexiones} / 10`}
                    sub={db.conexionesConsola > 0 ? `${db.conexionesConsola} de la consola (este sondeo)` : undefined} />
              <Stat label="Ficheros temporales" value={fmtNum(db.temporales.ficheros)}
                    sub={db.temporales.bytes ? bytes(db.temporales.bytes) : 'ninguno'} />
            </div>

            {db.interbloqueos > 0 && (
              <div className="alert warn" style={{ marginTop: 12 }}>
                {db.interbloqueos} interbloqueos desde el último reinicio de estadísticas.
              </div>
            )}

            {db.tablas_top?.length > 0 && (
              <div className="card" style={{ marginTop: 14 }}><div className="table-scroll">
                <table className="t">
                  <thead><tr><th>Tabla</th><th className="num">Tamaño</th><th className="num">Filas</th>
                    <th className="num" title="seq_scan + idx_scan">Escaneos</th><th className="num">Escrituras</th></tr></thead>
                  <tbody>
                    {db.tablas_top.map((t) => (
                      <tr key={t.tabla}>
                        <td style={{ fontFamily: 'var(--mono)' }}>{t.tabla}</td>
                        <td className="num">{bytes(t.bytes)}</td>
                        <td className="num">{fmtNum(t.filas)}</td>
                        <td className="num">{fmtNum(t.lecturas)}</td>
                        <td className="num">{fmtNum(t.escrituras)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            )}
          </>
        )}
      </div>

      {/* ── funciones ─────────────────────────────────────────────────────── */}
      <div className="section" style={{ marginTop: 26 }}>
        <div className="section-head">
          <h2 className="h2">Funciones</h2>
          <span className="hint">últimos {fns.dias} días{fns.topado ? ' · listado topado, los totales son un mínimo' : ''}</span>
        </div>

        {fns.error ? <div className="alert error">{fns.error}</div> : (
          <>
            <div className="grid-stats">
              <Stat label="Invocaciones" value={fmtNum(fns.total)} />
              <Stat label="Correctas" value={fmtNum(fns.ok)} />
              <Stat label="Con error" value={fmtNum(fns.fallo)}
                    sub={fns.total ? pct(fns.fallo / fns.total) : undefined} />
              <Stat label="Tiempo de cómputo" value={`${(fns.msTotal / 1000).toFixed(1)} s`} />
            </div>

            {fns.porFuncion?.length > 0 && (
              <div className="card" style={{ marginTop: 14 }}><div className="table-scroll">
                <table className="t">
                  <thead><tr><th>Función</th><th className="num">Invocaciones</th><th className="num">Errores</th>
                    <th className="num">Tiempo medio</th></tr></thead>
                  <tbody>
                    {fns.porFuncion.map((f) => (
                      <tr key={f.nombre}>
                        <td style={{ fontFamily: 'var(--mono)' }}>{f.nombre}</td>
                        <td className="num">{fmtNum(f.total)}</td>
                        <td className="num" style={{ color: f.fallo ? 'var(--danger)' : undefined }}>{fmtNum(f.fallo)}</td>
                        <td className="num">{f.total ? `${Math.round(f.ms / f.total)} ms` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            )}

            {fns.webhooks?.length > 0 && (
              <>
                <div className="field-label" style={{ marginTop: 16 }}>Invocaciones por webhook (acumuladas)</div>
                <div className="card"><div className="table-scroll">
                  <table className="t">
                    <thead><tr><th>Función</th><th className="num">Llamadas</th><th>Última</th></tr></thead>
                    <tbody>
                      {fns.webhooks.map((h) => (
                        <tr key={h.nombre}>
                          <td style={{ fontFamily: 'var(--mono)' }}>{h.nombre}</td>
                          <td className="num">{fmtNum(h.usos)}</td>
                          <td>{h.usadoAt ? relTime(h.usadoAt) : 'nunca'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div></div>
              </>
            )}
          </>
        )}
      </div>

      {/* ── aplicación ────────────────────────────────────────────────────── */}
      <div className="section" style={{ marginTop: 26 }}>
        <div className="section-head">
          <h2 className="h2">Aplicación</h2>
          {aplicacion.disponible && (
            <span className="hint">
              accesos vía NPM, últimos {aplicacion.ventanaDias} días
              {aplicacion.actualizado && ` · actualizado ${relTime(aplicacion.actualizado)}`}
            </span>
          )}
        </div>
        {!aplicacion.disponible ? (
          <div className="alert warn">
            Todavía no se miden los accesos a la aplicación: {aplicacion.motivo}. Se dice en vez de
            enseñar un cero, que se leería como «nadie ha entrado».
          </div>
        ) : (
          <>
            <div className="grid-stats">
              <Stat label="Peticiones" value={fmtNum(aplicacion.total)} />
              <Stat label="Errores 4xx" value={fmtNum(aplicacion.err4xx)}
                    sub={aplicacion.total ? pct(aplicacion.err4xx / aplicacion.total) : undefined} />
              <Stat label="Errores 5xx" value={fmtNum(aplicacion.err5xx)}
                    sub={aplicacion.total ? pct(aplicacion.err5xx / aplicacion.total) : undefined} />
              <Stat label="Transferido" value={bytes(aplicacion.bytes)} />
            </div>
            <div className="card" style={{ marginTop: 12 }}><div className="table-scroll">
              <table className="t">
                <thead><tr><th>Día</th><th className="num">Peticiones</th><th className="num">4xx</th>
                  <th className="num">5xx</th><th className="num">Transferido</th></tr></thead>
                <tbody>
                  {aplicacion.dias.map((d) => (
                    <tr key={d.dia}>
                      <td>{new Date(d.dia).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                      <td className="num">{fmtNum(d.peticiones)}</td>
                      <td className="num" style={{ color: d.err4xx ? 'var(--warning)' : undefined }}>{fmtNum(d.err4xx)}</td>
                      <td className="num" style={{ color: d.err5xx ? 'var(--danger)' : undefined }}>{fmtNum(d.err5xx)}</td>
                      <td className="num">{bytes(d.bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
          </>
        )}
      </div>
    </>
  )
}
